import { useId, useState } from 'react'
import { SnapshotCard } from '@/components/SnapshotCard'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DNS_FIELDS,
  DEFENDER_DNS_SLUG,
  DHCP_DNS_HEALTH_SLUG,
  NETWORK_ADAPTERS_SLUG,
  WINDOWS_FIREWALL_SLUG,
  adapterTypeLabel,
  formatIpList,
  healthStatusVariant,
  macAddressesFromHardware,
  normaliseAgentIpAddresses,
} from '@/lib/networkAdapters'
import { formatDateTime } from '@/lib/formatAgent'

function formatDate(iso) {
  return formatDateTime(iso)
}

function SnapshotField({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value)
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium break-all">{text}</span>
    </div>
  )
}

function HealthStatusBadge({ status }) {
  const text = status || 'Unknown'
  return <Badge variant={healthStatusVariant(status)}>{text}</Badge>
}

function DhcpScopeCard({ scope, index }) {
  const [open, setOpen] = useState(false)
  const summary = [
    scope.percent_in_use != null ? `${scope.percent_in_use}% in use` : null,
    scope.leases_free != null ? `${scope.leases_free} free` : null,
    scope.leases_bad ? `${scope.leases_bad} bad` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <CollapsePanel
      id={`dhcp-scope-${scope.scope_id || index}`}
      title={scope.name || scope.scope_id || `Scope ${index + 1}`}
      collapsedSummary={summary || scope.scope_id}
      open={open}
      onOpenChange={setOpen}
      pill={scope.scope_status || '—'}
      pillVariant={healthStatusVariant(scope.scope_status)}
      bodyClassName="space-y-2"
    >
      <SnapshotField label="Scope ID" value={scope.scope_id} />
      <SnapshotField label="Range" value={
        scope.start_range && scope.end_range
          ? `${scope.start_range} – ${scope.end_range}`
          : null
      }
      />
      <SnapshotField label="Subnet mask" value={scope.subnet_mask} />
      <SnapshotField label="State" value={scope.state} />
      <SnapshotField label="Leases in use" value={scope.leases_in_use} />
      <SnapshotField label="Free leases" value={scope.leases_free} />
      <SnapshotField label="Reserved" value={scope.leases_reserved} />
      <SnapshotField label="Bad / declined" value={scope.leases_bad} />
      <SnapshotField label="In use %" value={
        scope.percent_in_use != null ? `${scope.percent_in_use}%` : null
      }
      />
    </CollapsePanel>
  )
}

function AttentionItems({ items }) {
  const rows = Array.isArray(items) ? items.filter((i) => i?.detail) : []
  if (rows.length === 0) return null
  return (
    <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
      <p className="text-xs font-medium text-muted-foreground">Why this status</p>
      {rows.map((item, index) => (
        <div key={`${item.area || 'item'}-${index}`} className="space-y-1">
          <div className="flex flex-wrap items-baseline gap-2 text-sm">
            <HealthStatusBadge status={item.status} />
            <span className="font-medium">{item.area}</span>
          </div>
          <p className="text-sm text-muted-foreground break-words">{item.detail}</p>
          {item.action ? (
            <p className="text-sm break-words">
              <span className="font-medium">Recommended: </span>
              {item.action}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  )
}

function DhcpServerHealthCard({ dhcp, collectedAt }) {
  if (!dhcp?.role_installed) return null
  const scopes = Array.isArray(dhcp.scopes) ? dhcp.scopes : []
  const badSamples = Array.isArray(dhcp.bad_address_samples) ? dhcp.bad_address_samples : []
  const attention = Array.isArray(dhcp.attention) ? dhcp.attention : []

  return (
    <SnapshotCard
      title="DHCP server"
      collectedAt={collectedAt}
      pill={dhcp.overall_status}
      pillVariant={healthStatusVariant(dhcp.overall_status)}
      collapsedSummary={
        dhcp.interpretation ||
        `${dhcp.leases_free_total ?? '—'} free · ${dhcp.bad_addresses_total ?? 0} bad`
      }
      idPrefix="network-card"
    >
      <AttentionItems items={attention} />
      <SnapshotField label="Service" value={dhcp.service_status} />
      <SnapshotField label="AD authorisation" value={dhcp.authorization_note} />
      <SnapshotField label="Scopes" value={dhcp.scopes_total} />
      <SnapshotField label="Free leases (total)" value={dhcp.leases_free_total} />
      <SnapshotField label="In use (total)" value={dhcp.leases_in_use_total} />
      <SnapshotField label="Bad addresses (total)" value={dhcp.bad_addresses_total} />
      {scopes.length > 0 ? (
        <div className="space-y-2 pt-2">
          <p className="text-xs font-medium text-muted-foreground">Scopes</p>
          {scopes.map((scope, index) => (
            <DhcpScopeCard key={scope.scope_id || index} scope={scope} index={index} />
          ))}
        </div>
      ) : null}
      {badSamples.length > 0 ? (
        <div className="space-y-1 pt-2">
          <p className="text-xs font-medium text-muted-foreground">Bad address samples</p>
          {badSamples.map((row) => (
            <p key={`${row.scope_id}-${row.ip_address}`} className="text-xs break-all">
              {row.ip_address} ({row.address_state}) — {row.scope_name || row.scope_id}
              {row.hostname ? ` · ${row.hostname}` : ''}
            </p>
          ))}
        </div>
      ) : null}
    </SnapshotCard>
  )
}

function DnsServerHealthCard({ dns, collectedAt }) {
  if (!dns?.role_installed) return null
  const zones = Array.isArray(dns.zones) ? dns.zones : []
  const attention = Array.isArray(dns.attention) ? dns.attention : []

  return (
    <SnapshotCard
      title="DNS server"
      collectedAt={collectedAt}
      pill={dns.overall_status}
      pillVariant={healthStatusVariant(dns.overall_status)}
      collapsedSummary={dns.interpretation || `${dns.zones_total ?? 0} zones`}
      idPrefix="network-card"
    >
      <AttentionItems items={attention} />
      <SnapshotField label="Service" value={dns.service_status} />
      <SnapshotField label="Zones" value={dns.zones_total} />
      <SnapshotField label="Scavenging" value={
        dns.scavenging_enabled === true
          ? 'Enabled'
          : dns.scavenging_enabled === false
            ? 'Disabled'
            : '—'
      }
      />
      <SnapshotField label="Recursion" value={
        dns.recursion_enabled === true
          ? 'Enabled'
          : dns.recursion_enabled === false
            ? 'Disabled'
            : '—'
      }
      />
      <SnapshotField label="Forwarders" value={formatIpList(dns.forwarders)} />
      <SnapshotField label="Errors (24h)" value={dns.recent_errors_24h} />
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Resolution test</span>
        <HealthStatusBadge status={dns.resolution_test} />
      </div>
      {dns.resolution_test_detail ? (
        <p className="text-xs text-muted-foreground break-words">{dns.resolution_test_detail}</p>
      ) : null}
      {zones.length > 0 ? (
        <div className="space-y-1 pt-2">
          <p className="text-xs font-medium text-muted-foreground">Zones (sample)</p>
          {zones.slice(0, 12).map((zone) => (
            <p key={zone.zone_name} className="text-xs break-all">
              {zone.zone_name}
              {zone.is_paused ? ' · paused' : ''}
              {zone.is_shutdown ? ' · shut down' : ''}
            </p>
          ))}
          {zones.length > 12 ? (
            <p className="text-xs text-muted-foreground">+ {zones.length - 12} more</p>
          ) : null}
        </div>
      ) : null}
    </SnapshotCard>
  )
}

function DhcpDnsHealthSection({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const dhcp = data.dhcp
  const dns = data.dns
  const hasDhcp = Boolean(dhcp?.role_installed)
  const hasDns = Boolean(dns?.role_installed)

  if (!hasDhcp && !hasDns) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6 text-sm text-muted-foreground">
          DHCP/DNS health snapshot received, but neither server role is installed on this host.
          Schedule <span className="font-medium">dhcp-dns-health</span> on your DHCP and DNS servers only.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">DHCP and DNS server roles</h3>
        <p className="text-xs text-muted-foreground">
          {data.overall_status ? `Overall ${data.overall_status}` : ''}
          {snapshot.collected_at ? ` · collected ${formatDate(snapshot.collected_at)}` : ''}
        </p>
      </div>
      {data.interpretation ? (
        <p className="text-sm text-muted-foreground">{data.interpretation}</p>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        {hasDhcp ? (
          <DhcpServerHealthCard dhcp={dhcp} collectedAt={snapshot.collected_at} />
        ) : null}
        {hasDns ? (
          <DnsServerHealthCard dns={dns} collectedAt={snapshot.collected_at} />
        ) : null}
      </div>
    </div>
  )
}

function FirewallProfileBadge({ enabled }) {
  if (enabled === true) return <Badge variant="success">On</Badge>
  if (enabled === false) return <Badge variant="destructive">Off</Badge>
  return <span className="font-medium">—</span>
}

function DnsCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  return (
    <SnapshotCard title="DNS" collectedAt={snapshot.collected_at}>
      {Object.entries(DNS_FIELDS).map(([key, label]) => (
        <SnapshotField key={key} label={label} value={data[key]} />
      ))}
    </SnapshotCard>
  )
}

function WindowsFirewallCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const profiles = Array.isArray(data.profiles) ? data.profiles : []
  const fallbackProfiles = [
    { name: 'Domain', key: 'domain_profile' },
    { name: 'Private', key: 'private_profile' },
    { name: 'Public', key: 'public_profile' },
  ]
  const rows =
    profiles.length > 0
      ? profiles
      : fallbackProfiles.map(({ name, key }) => ({
          name,
          enabled:
            data[key] === true || data[key] === 'On' || data[key] === 'Enabled',
        }))

  return (
    <SnapshotCard title="Windows Firewall" collectedAt={snapshot.collected_at}>
      {rows.length > 0 ? (
        <div className="space-y-2">
          {rows.map((profile) => (
            <div key={profile.name} className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">{profile.name} profile</span>
              <FirewallProfileBadge enabled={profile.enabled} />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No firewall profile data reported.</p>
      )}
    </SnapshotCard>
  )
}

function IdentityCard({ agent, hardware }) {
  const [open, setOpen] = useState(false)
  const ateraIps = normaliseAgentIpAddresses(agent?.ip_addresses)
  const macs = macAddressesFromHardware(hardware)
  const summaryBits = [
    agent?.reported_from_ip ? `Reported ${agent.reported_from_ip}` : null,
    ateraIps.length ? `${ateraIps.length} Atera IP(s)` : null,
    macs.length ? `${macs.length} MAC(s)` : null,
  ].filter(Boolean)

  return (
    <CollapsePanel
      id="network-identity"
      title="Reported addresses"
      collapsedSummary={summaryBits.join(' · ') || 'No IPs reported from Atera yet'}
      open={open}
      onOpenChange={setOpen}
      bodyClassName="space-y-2"
    >
      <SnapshotField label="Reported from IP" value={agent?.reported_from_ip} />
      <SnapshotField label="Atera IP addresses" value={formatIpList(ateraIps)} />
      <SnapshotField label="MAC addresses" value={formatIpList(macs)} />
    </CollapsePanel>
  )
}

function AdapterCard({ adapter, index }) {
  const [open, setOpen] = useState(false)
  const typeLabel = adapterTypeLabel(adapter.type)
  const ipSummary = formatIpList(adapter.ipv4_addresses)
  const status = String(adapter.status || '').toLowerCase()
  const up = status === 'up'
  const summary = [
    typeLabel,
    adapter.ssid ? `SSID ${adapter.ssid}` : null,
    ipSummary !== '—' ? ipSummary : 'No IPv4',
    adapter.gateway ? `GW ${adapter.gateway}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <CollapsePanel
      id={`network-adapter-${index}-${adapter.name || 'adapter'}`}
      title={adapter.name || adapter.description || `Adapter ${index + 1}`}
      collapsedSummary={summary}
      open={open}
      onOpenChange={setOpen}
      pill={up ? 'Up' : adapter.status || 'Unknown'}
      pillVariant={up ? 'ready' : 'inactive'}
      bodyClassName="space-y-2"
    >
      <SnapshotField label="Description" value={adapter.description} />
      <SnapshotField label="Type" value={typeLabel} />
      <SnapshotField label="Status" value={adapter.status} />
      <SnapshotField label="Wi-Fi name (SSID)" value={adapter.ssid} />
      <SnapshotField label="Connection profile" value={adapter.connection_profile} />
      <SnapshotField label="MAC address" value={adapter.mac_address} />
      <SnapshotField label="IPv4 addresses" value={formatIpList(adapter.ipv4_addresses)} />
      <SnapshotField label="IPv4 prefixes" value={formatIpList(adapter.ipv4_prefixes)} />
      <SnapshotField label="IPv6 addresses" value={formatIpList(adapter.ipv6_addresses)} />
      <SnapshotField label="Gateway" value={adapter.gateway} />
      <SnapshotField label="DNS servers" value={formatIpList(adapter.dns_servers)} />
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">DHCP</span>
        {adapter.dhcp_enabled === true ? (
          <Badge variant="success">Enabled</Badge>
        ) : adapter.dhcp_enabled === false ? (
          <Badge variant="secondary">Disabled</Badge>
        ) : (
          <span className="font-medium">—</span>
        )}
      </div>
    </CollapsePanel>
  )
}

function AdaptersSection({ snapshot }) {
  const data = snapshot?.payload || {}
  const adapters = Array.isArray(data.adapters) ? data.adapters : []
  const count = data.adapter_count ?? adapters.length

  if (!snapshot) {
    return (
      <Card className="border-dashed">
        <CardContent className="pt-6 text-sm text-muted-foreground">
          No adapter inventory yet. Deploy the <span className="font-medium">network-adapters</span>{' '}
          RMM script from Atera (run as System) to report IPs, gateway, wired/Wi-Fi type, and SSID.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Network adapters</h3>
        <p className="text-xs text-muted-foreground">
          {count} adapter{count === 1 ? '' : 's'}
          {snapshot.collected_at ? ` · collected ${formatDate(snapshot.collected_at)}` : ''}
        </p>
      </div>
      {adapters.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Snapshot received, but no adapters were listed.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {adapters.map((adapter, index) => (
            <AdapterCard
              key={`${adapter.name || adapter.mac_address || index}-${index}`}
              adapter={adapter}
              index={index}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AteraConnectivityCard({ agent }) {
  const online = agent?.online_status
  const onlineLabel =
    online === true ? 'Online' : online === false ? 'Offline' : 'Unknown'

  return (
    <Card
      id="network-atera-connectivity"
      className={online === false ? 'border-amber-300/80 bg-amber-50/40' : undefined}
    >
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">Agent online status</h3>
          <Badge
            variant={
              online === true ? 'success' : online === false ? 'warning' : 'secondary'
            }
          >
            {onlineLabel}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Whether the Atera agent last reported as reachable. Adapter, DNS, and firewall
          detail is below.
        </p>
        {online === false ? (
          <p className="text-sm text-amber-900">
            The agent is currently offline — confirm the device is powered on and the
            Atera agent service is running.
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function AgentNetwork({ agent, hardware = [], loading, data }) {
  if (loading) {
    return <Skeleton className="h-48 w-full" />
  }

  const snapshots = data?.snapshots ?? []
  const adaptersSnap = snapshots.find((s) => s.script_slug === NETWORK_ADAPTERS_SLUG)
  const firewallSnap = snapshots.find((s) => s.script_slug === WINDOWS_FIREWALL_SLUG)
  const defenderDns = snapshots.find((s) => s.script_slug === DEFENDER_DNS_SLUG)
  const dhcpDnsSnap = snapshots.find((s) => s.script_slug === DHCP_DNS_HEALTH_SLUG)
  const hasDns = Boolean(defenderDns?.payload?.dns1 || defenderDns?.payload?.dns2)
  const hasIdentity =
    Boolean(agent?.reported_from_ip) ||
    normaliseAgentIpAddresses(agent?.ip_addresses).length > 0 ||
    macAddressesFromHardware(hardware).length > 0

  const hasAny =
    adaptersSnap || firewallSnap || hasDns || hasIdentity || defenderDns || dhcpDnsSnap

  return (
    <div className="space-y-6">
      <AteraConnectivityCard agent={agent} />

      {!hasAny ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No adapter, DNS, or firewall data for this device yet. Deploy the
            network-adapters, windows-firewall, and defender-dns RMM scripts from Atera.
            On DHCP/DNS servers, also schedule dhcp-dns-health.
          </CardContent>
        </Card>
      ) : (
        <>
          {hasIdentity ? (
            <div className="grid gap-4 md:grid-cols-2">
              <IdentityCard agent={agent} hardware={hardware} />
            </div>
          ) : null}

          {dhcpDnsSnap ? <DhcpDnsHealthSection snapshot={dhcpDnsSnap} /> : null}

          <AdaptersSection snapshot={adaptersSnap} />

          {(hasDns || firewallSnap) && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">DNS and firewall</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {hasDns ? <DnsCard snapshot={defenderDns} /> : null}
                <WindowsFirewallCard snapshot={firewallSnap} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
